import { AccessControlManager } from "../_resources/accesscontrol-manager.js";
import { getSupaACLLobby } from "../_resources/supa-helpers.js";
import { supabaseClient } from "../auth/supabase.service.js";
import { holdButton } from "../_resources/ui-library.js";
import { runWaffleSyncACL } from "../_resources/waffle-helpers.js";
import "./LobbyView.styl";

export default class LobbyView {
  constructor({ dom, message }) {
    this.dom = dom;
    this.message = message;
    this.container = null;
    this.lobbyData = [];
  }

  async loadLobbyData() {
    const { ok, error } = await getSupaACLLobby();
    
    if (error) {
      console.error('Error loading lobby data:', error);
      this.message.bad(error);
      return { error };
    }
    
    this.lobbyData = ok;
    return { data: this.lobbyData };
  }

  async render() {
    const container = this.dom;
    this.container = container;
    
    container.html('');
    container.append('h1').text('Approve Access Requests');
    
    // Get current user
    const { data: { user } } = await supabaseClient.auth.getUser();
    this.currentUserUuid = user?.id;
    
    const { error } = await this.loadLobbyData();
    if (error) return;
    
    if (this.lobbyData.length === 0) {
      container.append('p')
        .style('color', '#999')
        .text('No pending access requests.');
      return;
    }
    
    // Check if current user has any pending requests
    const hasOwnRequests = this.lobbyData.some(req => req.user_uuid === this.currentUserUuid);
    
    // Add "Grant All" button at the top (hide if user has own requests)
    if (!hasOwnRequests) {
      this.renderGrantAllButton(container);
    }
    
    // Group by email domain, then by email
    const grouped = this.groupByDomain(this.lobbyData);
    
    // Sort domains alphabetically
    const sortedDomains = Object.keys(grouped).sort();
    
    sortedDomains.forEach((domain) => {
      this.renderDomainSection(container, domain, grouped[domain]);
    });
  }

  renderGrantAllButton(container) {
    const buttonContainer = container.append('div')
      .style('margin-bottom', '20px')
      .style('display', 'flex')
      .style('gap', '10px');
    
    // Check if any requests have unset levels
    const hasUnsetLevels = this.lobbyData.some(req => !req.level);
    
    const grantAllBtn = buttonContainer.append('button')
      .attr('class', 'button addnew hold-btn')
      .property('disabled', hasUnsetLevels)
      .text('✓ Grant All Requests');
    
    if (!hasUnsetLevels) {
      holdButton(grantAllBtn, async () => {
        await this.grantAllRequests();
      });
    }
    
    const rejectAllBtn = buttonContainer.append('button')
      .attr('class', 'button delete hold-btn')
      .text('✘ Reject All Requests');
    
    holdButton(rejectAllBtn, async () => {
      await this.rejectAllRequests();
    });
  }

  async grantAllRequests() {
    this.message.progress(`Granting ${this.lobbyData.length} access requests...`);
    
    let successCount = 0;
    let errorCount = 0;
    
    for (const request of this.lobbyData) {
      const level = request.level;
      
      const { error } = await supabaseClient.rpc('grant_acl_by_email', {
        _email: request.email,
        _scope: request.scope,
        _resource: request.resource,
        _level: level
      });
      
      if (error) {
        console.error('Failed to grant:', request, error);
        errorCount++;
      } else {
        successCount++;
        
        // Remove from lobby
        await supabaseClient
          .from('acl_lobby')
          .delete()
          .eq('user_uuid', request.user_uuid)
          .eq('scope', request.scope)
          .eq('resource', request.resource);
      }
    }
    
    if (errorCount > 0) {
      this.message.bad(`Granted ${successCount}, failed ${errorCount}`);
    } else {
      this.message.good(`Granted all ${successCount} requests`);
    }

    if(successCount > 0) runWaffleSyncACL();
    setTimeout(() => this.render(), 1000);
  }

  async rejectAllRequests() {
    this.message.progress(`Rejecting ${this.lobbyData.length} access requests...`);
    
    let successCount = 0;
    let errorCount = 0;
    
    for (const request of this.lobbyData) {
      const { error } = await supabaseClient
        .from('acl_lobby')
        .delete()
        .eq('user_uuid', request.user_uuid)
        .eq('scope', request.scope)
        .eq('resource', request.resource);
      
      if (error) {
        console.error('Failed to reject:', request, error);
        errorCount++;
      } else {
        successCount++;
      }
    }
    
    if (errorCount > 0) {
      this.message.bad(`Rejected ${successCount}, failed ${errorCount}`);
    } else {
      this.message.good(`Rejected all ${successCount} requests`);
    }
    
    setTimeout(() => this.render(), 1000);
  }

  groupByDomain(data) {
    const grouped = {};
    
    data.forEach(item => {
      const domain = item.email.split('@')[1] || 'unknown';
      if (!grouped[domain]) {
        grouped[domain] = {};
      }
      if (!grouped[domain][item.email]) {
        grouped[domain][item.email] = [];
      }
      grouped[domain][item.email].push(item);
    });
    
    return grouped;
  }

  renderDomainSection(container, domain, users) {
    const section = container.append('div')
      .attr('class', 'lobby-domain-section');
    
    // Sort emails alphabetically
    const sortedEmails = Object.keys(users).sort();
    
    sortedEmails.forEach((email) => {
      this.renderUserSection(section, email, users[email]);
    });
  }

  renderUserSection(container, email, requestList) {
    const self = this;
    const userSection = container.append('div')
      .attr('class', 'lobby-user-section');
    
    const headerContainer = userSection.append('div')
      .style('display', 'flex')
      .style('align-items', 'center')
      .style('gap', '15px')
      .style('margin-bottom', '10px');
    
    headerContainer.append('h3')
      .style('margin', '0')
      .text(`${email}: ${requestList.length} request${requestList.length !== 1 ? 's' : ''}`);
    
    // Per-user grant/reject buttons
    const userActionsBox = headerContainer.append('div').attr('class', 'actionsbox');
    
    const isCurrentUserSection = requestList.length > 0 && requestList[0].user_uuid === self.currentUserUuid;
    const hasUnsetLevels = requestList.some(req => !req.level);
    
    // Hide Grant All button for current user's own requests
    if (!isCurrentUserSection) {
      const grantUserBtn = userActionsBox.append('button')
        .attr('class', 'button addnew hold-btn')
        .property('disabled', hasUnsetLevels)
        .text('✓ Grant All');
      
      if (!hasUnsetLevels) {
        holdButton(grantUserBtn, async () => {
          await self.grantUserRequests(email, requestList);
        });
      }
    }
    
    const rejectUserBtn = userActionsBox.append('button')
      .attr('class', 'button delete hold-btn')
      .text(isCurrentUserSection ? '✘ Withdraw All' : '✘ Reject All');
    
    holdButton(rejectUserBtn, async () => {
      await self.rejectUserRequests(email, requestList);
    });
    
    const tableContainer = userSection.append('div');
    
    // Sort requests by scope then resource
    const sortedRequests = requestList.sort((a, b) => {
      if (a.scope !== b.scope) return a.scope.localeCompare(b.scope);
      return a.resource.localeCompare(b.resource);
    });
    
    // Create custom table with editable levels and grant buttons
    const table = tableContainer.append('table').attr('class', 'admin-table');
    
    // Header
    table.append('tr')
      .each(function() {
        const rowEl = d3.select(this);
        rowEl.append('th').text('Scope');
        rowEl.append('th').text('Resource');
        rowEl.append('th').text('Requested Level');
        rowEl.append('th').text('Actions');
      });
    
    // Rows
    table.selectAll('tr.item')
      .data(sortedRequests)
      .enter().append('tr')
      .attr('class', 'item')
      .each(function(row) {
        const rowEl = d3.select(this);
        
        rowEl.append('td').text(row.scope);
        rowEl.append('td').text(row.resource);
        
        // Editable level cell
        const levelCell = rowEl.append('td').attr('class', 'editable');
        const levelText = row.level ? self.emojifyLevel(row.level) : '(not specified)';
        levelCell.append('span').attr('class', 'static').text(levelText);
        
        levelCell.on('click', function(event) {
          if (event) event.stopPropagation();
          self.editLevel(d3.select(this), row);
        });
        
        // Actions: Grant and Reject buttons
        const actionsCell = rowEl.append('td');
        const actionsBox = actionsCell.append('div').attr('class', 'actionsbox');
        
        // Hide grant button if this is the current user's request
        const isCurrentUser = row.user_uuid === self.currentUserUuid;
        
        if (!isCurrentUser) {
          const grantBtn = actionsBox.append('button')
            .attr('class', 'button addnew hold-btn')
            .property('disabled', !row.level)
            .text('✓');
          
          if (row.level) {
            holdButton(grantBtn, async () => {
              await self.grantRequest(row);
            });
          }
        }
        
        const rejectBtn = actionsBox.append('button')
          .attr('class', 'button delete hold-btn')
          .text('✘');
        
        holdButton(rejectBtn, async () => {
          await self.rejectRequest(row);
        });
      });
  }

  emojifyLevel(level) {
    const emoji = level === "reader" ? "👀" : level === "editor" ? "✏️" : level === "owner" ? "👑" : "";
    return `${emoji} ${level}`;
  }

  editLevel(cell, row) {
    // Prevent re-entering edit if already editing
    if (cell.classed('editing')) return;
    
    cell.classed('editing', true);
    cell.selectAll('*').remove();
    
    const wrapper = cell.append('div');
    
    const roles = ['reader', 'editor', 'owner'];
    const select = wrapper.append('select')
      .attr('class', 'input')
      .style('width', '50%')
      .on('click', (e) => { if (e) e.stopPropagation(); });
    
    roles.forEach(r => {
      select.append('option')
        .attr('value', r)
        .property('selected', r === (row.level || 'reader'))
        .text(this.emojifyLevel(r));
    });
    
    const actions = wrapper.append('span')
      .attr('class', 'actionsbox')
      .on('click', (e) => { if (e) e.stopPropagation(); });
    
    const saveBtn = actions.append('button').attr('class', 'button addnew').text('Save');
    const cancelBtn = actions.append('button').attr('class', 'button delete').text('Cancel');
    
    const resetView = () => {
      cell.classed('editing', false).selectAll('*').remove();
      const levelText = row.level ? this.emojifyLevel(row.level) : '(not specified)';
      cell.append('span').attr('class', 'static').text(levelText);
    };
    
    cancelBtn.on('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      resetView();
    });
    
    saveBtn.on('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      const newLevel = select.property('value');
      
      // Update level in acl_lobby table
      const { error } = await supabaseClient
        .from('acl_lobby')
        .update({ level: newLevel })
        .eq('user_uuid', row.user_uuid)
        .eq('scope', row.scope)
        .eq('resource', row.resource);
      
      if (error) {
        this.message.bad('Failed to update level');
        console.error('Failed to update level:', error);
        resetView();
      } else {
        row.level = newLevel;
        this.message.good('Level updated');
        // Re-render to enable grant button and update grant all button
        setTimeout(() => this.render(), 300);
      }
    });
  }

  async grantRequest(row) {
    if (!row.level) {
      this.message.bad('Please set access level first');
      return;
    }
    
    this.message.progress('Granting access...');
    
    const { error } = await supabaseClient.rpc('grant_acl_by_email', {
      _email: row.email,
      _scope: row.scope,
      _resource: row.resource,
      _level: row.level
    });
    
    if (error) {
      this.message.bad(error.message || 'Grant failed');
    } else {
      // Remove from lobby
      const { error: deleteError } = await supabaseClient
        .from('acl_lobby')
        .delete()
        .eq('user_uuid', row.user_uuid)
        .eq('scope', row.scope)
        .eq('resource', row.resource);
      
      if (deleteError) {
        this.message.bad('Access granted but failed to remove from lobby');
      } else {
        this.message.good('Access granted');
        setTimeout(() => this.render(), 1000);
      }
    }
  }

  async rejectRequest(row) {
    this.message.progress('Rejecting request...');
    
    const { error } = await supabaseClient
      .from('acl_lobby')
      .delete()
      .eq('user_uuid', row.user_uuid)
      .eq('scope', row.scope)
      .eq('resource', row.resource);
    
    if (error) {
      this.message.bad(error.message || 'Reject failed');
    } else {
      this.message.good('Request rejected');
      setTimeout(() => this.render(), 1000);
    }
  }

  async grantUserRequests(email, requestList) {
    this.message.progress(`Granting ${requestList.length} request${requestList.length !== 1 ? 's' : ''} for ${email}...`);
    
    let successCount = 0;
    let errorCount = 0;
    
    for (const request of requestList) {
      const { error } = await supabaseClient.rpc('grant_acl_by_email', {
        _email: request.email,
        _scope: request.scope,
        _resource: request.resource,
        _level: request.level
      });
      
      if (error) {
        console.error('Failed to grant:', request, error);
        errorCount++;
      } else {
        successCount++;
        
        // Remove from lobby
        await supabaseClient
          .from('acl_lobby')
          .delete()
          .eq('user_uuid', request.user_uuid)
          .eq('scope', request.scope)
          .eq('resource', request.resource);
      }
    }
    
    if (errorCount > 0) {
      this.message.bad(`Granted ${successCount}, failed ${errorCount}`);
    } else {
      this.message.good(`Granted all ${successCount} requests for ${email}`);
    }

    if(successCount > 0) runWaffleSyncACL();
    setTimeout(() => this.render(), 1000);
  }

  async rejectUserRequests(email, requestList) {
    this.message.progress(`Rejecting ${requestList.length} request${requestList.length !== 1 ? 's' : ''} for ${email}...`);
    
    let successCount = 0;
    let errorCount = 0;
    
    for (const request of requestList) {
      const { error } = await supabaseClient
        .from('acl_lobby')
        .delete()
        .eq('user_uuid', request.user_uuid)
        .eq('scope', request.scope)
        .eq('resource', request.resource);
      
      if (error) {
        console.error('Failed to reject:', request, error);
        errorCount++;
      } else {
        successCount++;
      }
    }
    
    if (errorCount > 0) {
      this.message.bad(`Rejected ${successCount}, failed ${errorCount}`);
    } else {
      this.message.good(`Rejected all ${successCount} requests for ${email}`);
    }
    
    setTimeout(() => this.render(), 1000);
  }
}
